import type { RateChange } from './types';

const BASE = '/api/salary';

function authHeaders(sessionToken: string | null): HeadersInit {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (sessionToken) h['X-Session-Token'] = sessionToken;
  return h;
}

export async function apiGet<T = any>(path: string, sessionToken: string | null): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { headers: authHeaders(sessionToken), cache: 'no-store' });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `GET ${path} failed`);
  return data;
}

export async function apiSend<T = any>(
  path: string,
  method: 'POST' | 'PUT' | 'DELETE',
  body: any,
  sessionToken: string | null
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: authHeaders(sessionToken),
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `${method} ${path} failed`);
  return data;
}

// ---- Effective-dated pay-rate changes ----
export async function getRateChanges(employeeId: number, sessionToken: string | null) {
  return apiGet<{ success: boolean; rate_changes: RateChange[] }>(
    `/rate-changes?employee_id=${employeeId}`,
    sessionToken
  );
}

export async function createRateChange(
  payload: { employee_id: number; effective_date: string; hourly_rate: number; note?: string | null },
  sessionToken: string | null
) {
  return apiSend<{ success: boolean; rate_change: RateChange; entries_updated: number }>(
    '/rate-changes',
    'POST',
    payload,
    sessionToken
  );
}

export async function deleteRateChange(id: number, sessionToken: string | null) {
  return apiSend<{ success: boolean; entries_updated: number }>(
    `/rate-changes/${id}`,
    'DELETE',
    undefined,
    sessionToken
  );
}

// Session token is appended as a query param so that target="_blank" anchor
// links (which can't set custom headers) still authenticate. The download
// endpoint reads it via req.query.token in the auth middleware.
export const paystubUrl = (paymentId: number, sessionToken: string | null) => {
  const base = `${BASE}/payments/${paymentId}/paystub`;
  return sessionToken ? `${base}?token=${encodeURIComponent(sessionToken)}` : base;
};
